import { DateTime } from 'luxon'

import { SAT_BOOTCAMP_ONBOARDING_TOPIC } from '@/queries/constants'
import SATSeriesQ from '@/queries/SATSeriesQ'
import SATTestInterventionArmQ from '@/queries/SATTestInterventionArmQ'
import SATTestInterventionArmTaskQ from '@/queries/SATTestInterventionArmTaskQ'
import SATTestQ from '@/queries/SATTestQ'
import SATUserQ from '@/queries/SATUserQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesTagQ from '@/queries/SeriesTagQ'
import SeriesTemplateQ from '@/queries/SeriesTemplateQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { type Query, SQL, type SQLFragment } from '@/client/query'
import { SERIES_RESERVATIONS_MAX } from '@/client/services/series/constants'
import { SeriesTag } from '@/client/services/tags/types'
import { LevelRequirementStatus } from '@/client/services/worlds/progression/enum'

import type {
  SATAssessmentsVersion,
  SATBootcampInterventionArmDetails,
  SATBootcampInterventionTaskDetails,
  SATBootcampSeriesDetails,
  SATBootcampTestDetails,
} from './types'

export function satBootcampInterventionTaskDetailsFromQuery(
  task: Query
): SATBootcampInterventionTaskDetails {
  const dueDate = task(SATTestInterventionArmTaskQ.due_date)
  return {
    id: task(SATTestInterventionArmTaskQ.id) ?? 0,
    name: task(SATTestInterventionArmTaskQ.name) ?? '',
    releaseDate: DateTime.fromISO(`${task(SATTestInterventionArmTaskQ.release_date)}`),
    dueDate: dueDate == null ? null : DateTime.fromISO(dueDate),
    link: task(SATTestInterventionArmTaskQ.link) ?? '',
    satTestId: task(SATTestInterventionArmQ.sat_test_id) ?? 0,
    satTestInterventionArmId: task(SATTestInterventionArmTaskQ.sat_test_intervention_arm_id) ?? 0,
    reminderHistory: (task(SATTestInterventionArmTaskQ.reminder_history) ?? []).map(
      reminderTimestamp => DateTime.fromISO(reminderTimestamp)
    ),
    forLearners: task(SATTestInterventionArmTaskQ.for_learners) ?? false,
  }
}

export function satBootcampInterventionArmFromQuery(
  interventionArm: Query
): SATBootcampInterventionArmDetails {
  const tasks = interventionArm.many(
    SATTestInterventionArmTaskQ.where({
      satTestInterventionArmId: SATTestInterventionArmQ.id,
      order: [[SATTestInterventionArmTaskQ.release_date, 'asc']],
    })
  )
  return {
    id: interventionArm(SATTestInterventionArmQ.id) ?? 0,
    name: interventionArm(SATTestInterventionArmQ.name) ?? '',
    group: interventionArm(SATTestInterventionArmQ.group_slug) ?? '',
    userType: interventionArm(SATTestInterventionArmQ.user_type) ?? 'learner',
    weight: interventionArm(SATTestInterventionArmQ.weight) ?? 0,
    satTestTitle: interventionArm(SATTestQ.title) ?? '',
    countUsers: interventionArm(SATTestInterventionArmQ.countUsers()) ?? 0,
    createdAt: DateTime.fromISO(`${interventionArm(SATTestInterventionArmQ.created_at)}`),
    tasks: tasks.map(satBootcampInterventionTaskDetailsFromQuery),
  }
}

export function satTestDetailsFromQuery(satTest: Query): SATBootcampTestDetails {
  return {
    title: satTest(SATTestQ.title) ?? '',
    id: satTest(SATTestQ.id) ?? 0,
    testDate: DateTime.fromISO(`${satTest(SATTestQ.test_date)}`),
    tutorReleasedAt: DateTime.fromISO(`${satTest(SATTestQ.tutor_released_at)}`),
    seriesCreationDeadline: DateTime.fromISO(`${satTest(SATTestQ.series_creation_deadline)}`),
    learnerReleasedAt: DateTime.fromISO(`${satTest(SATTestQ.learner_released_at)}`),
    learnerRegistrationDeadline: DateTime.fromISO(
      `${satTest(SATTestQ.learner_registration_deadline)}`
    ),
    onboardingSessionReleasedAt: DateTime.fromISO(
      `${satTest(SATTestQ.onboarding_session_released_at)}`
    ),
    onboardingSessionDeadline: DateTime.fromISO(`${satTest(SATTestQ.onboarding_session_deadline)}`),
    startDate: DateTime.fromISO(`${satTest(SATTestQ.series_start_date_min)}`),
    endDate: DateTime.fromISO(`${satTest(SATTestQ.series_end_date_max)}`),
  }
}

// December 15 2023
const DIGITAL_SAT_V1_TRANSITION_DATE = DateTime.local(2023, 12, 15).toJSDate()

// September 1, 2025
const DIGITAL_SAT_V2_TRANSITION_DATE = DateTime.local(2025, 9, 1).toJSDate()

export function getSATAssessmentsVersion(seriesStartDate: Date): SATAssessmentsVersion {
  if (seriesStartDate > DIGITAL_SAT_V2_TRANSITION_DATE) {
    return 'dsat-2'
  }
  if (seriesStartDate > DIGITAL_SAT_V1_TRANSITION_DATE) {
    return 'dsat-1'
  }

  return 'original'
}

export function getOnboardingSessionHostSoftDeadline({
  onboardingSessionDeadline,
  tutorRegistrationDeadline,
}: {
  onboardingSessionDeadline: DateTime
  tutorRegistrationDeadline: DateTime
}): DateTime {
  return tutorRegistrationDeadline > DateTime.now()
    ? tutorRegistrationDeadline
    : onboardingSessionDeadline
}

const EARLY_REGISTRATION_DEADLINE_DAYS_FROM_OPEN = 6

export function getEarlyTutorRegistrationDeadline({
  tutorRegistrationOpens,
}: {
  tutorRegistrationOpens: DateTime
}): DateTime {
  return tutorRegistrationOpens.plus({ days: EARLY_REGISTRATION_DEADLINE_DAYS_FROM_OPEN })
}

export function satBootcampSeriesDetailsFromQuery(series: Query): SATBootcampSeriesDetails {
  const sessionStartDates = series
    .many(SessionQ.where({ seriesId: SeriesQ.id, order: [[SessionQ.start_date, 'asc']] }))
    .map(sessionItem => `${sessionItem(SessionQ.start_date)}`)
  const seriesTemplate = series.one(SeriesQ.getSeriesTemplate())

  return {
    id: series(SeriesQ.id) ?? 0,
    satTestId: series(SATSeriesQ.sat_test_id) ?? 0,
    sessionStartDates,
    tutorName: series(UserQ.display_name) ?? '',
    tutorAvatarUrl: series(UserQ.avatar_url) ?? '',
    templateSlug: seriesTemplate(SeriesTemplateQ.slug) ?? '',
    tutorBio: series(UserQ.bio) ?? '',
    satScoreBandName: series(SeriesQ.getScoreBandName()) ?? '',
    countRegistrationsAndReservations:
      series(SeriesQ.countRegistrationsAndActiveReservations()) ?? 0,
    maxLearners: series(SeriesQ.max_learners) ?? 0,
    researchGroup: series(SeriesQ.getResearchGroup()),
    attendancePolicy: series(SeriesQ.attendance_policy) ?? '',
    questionnaire: series(SeriesQ.questionnaire) ?? null,
  }
}

export function getSATSeriesSpotsAvailableToReserve({
  series,
}: {
  series?: Pick<
    SATBootcampSeriesDetails,
    'maxLearners' | 'countRegistrationsAndReservations'
  > | null
}): number {
  if (series == null) {
    return SERIES_RESERVATIONS_MAX
  }

  return Math.min(
    SERIES_RESERVATIONS_MAX,
    Math.max(0, series.maxLearners - series.countRegistrationsAndReservations - 1)
  )
}

export function getOnboardingSessionEligibilityStatus({
  userId,
}: {
  userId: number | SQLFragment<number>
}): SQLFragment<LevelRequirementStatus> {
  return SQL<LevelRequirementStatus>`
    (
      CASE WHEN ${SessionHostQ.hasTutoredSessionWithTopicAndAtLeastOneLearner({
        userId,
        topicName: SAT_BOOTCAMP_ONBOARDING_TOPIC,
      })}
        THEN ${LevelRequirementStatus.Complete}
        WHEN EXISTS(SELECT 1 ${SessionQ.where(
          {
            tutor: userId,
            include: [
              {
                association: 'Topic',
                required: true,
                where: SQL`${TopicQ.name} = ${SAT_BOOTCAMP_ONBOARDING_TOPIC}`,
              },
            ],
          },
          SQL`${SessionQ.start_date} > NOW()`
        )})
        THEN ${LevelRequirementStatus.InProgress}
        WHEN NOT EXISTS(SELECT 1 ${SeriesTagQ.where({
          tag: SeriesTag.HighlyEngagingSATBootcamp,
          include: [
            {
              association: 'Series',
              required: true,
              where: SQL`${SeriesQ.user_id} = ${userId}`,
            },
          ],
        })})
        AND NOT EXISTS(SELECT 1 ${SATUserQ.where(
          { userId },
          SATUserQ.hasEngagingBootcampRequirementWaived()
        )})
        THEN ${LevelRequirementStatus.Locked}
        ELSE ${LevelRequirementStatus.Incomplete}
      END
    )
  `
}
